import React, { useEffect } from 'react'
import { Card, Typography, Space, Button } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpandArrowsAlt, faCompressArrowsAlt, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons'
import ServiceCategory from './components/ServiceCategory'
import useServices from './hooks/useServices'
import { getCategories } from './utils'
import { GlobalServicesEnums } from '../../../../../Super-Admin/Global Services/utils/enums'

const { Title, Text } = Typography

/**
 * Services Offered Tab Component
 * Displays and manages the services offered by a clinic
 */
const ServicesOfferedTab = ({ servicesOfferedForm, setRecordData, recordData, handleValuesChange }) => {
  // Get initial active services from form

  // Initialize services hook
  const {
    activeServices,
    expandedCategories,
    activeServicesCount,
    globalServices,
    categoryCount,
    toggleService,
    toggleCategoryExpansion,
    expandAllCategories,
    collapseAllCategories,
    clearAllServices,
    activateAllServices,
    handleToggleAllCategoryServices
  } = useServices({ recordData })

  // Update form when active services change
  useEffect(() => {
    // Only update if activeServices is different from current form values
    const currentFormValue = servicesOfferedForm.getFieldValue('servicesOffered') || []
    const activeServicesSet = new Set(activeServices)
    const currentFormSet = new Set(currentFormValue)

    // Check if the arrays are different
    const isDifferent =
      activeServices.length !== currentFormValue.length ||
      !activeServices.every((id) => currentFormSet.has(id)) ||
      !currentFormValue.every((id) => activeServicesSet.has(id))

    if (isDifferent) {
      handleValuesChange({ servicesOffered: activeServices })
      servicesOfferedForm.setFieldsValue({ servicesOffered: activeServices })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeServices])

  // Check if all categories are expanded
  const allCategoriesExpanded = expandedCategories.length === categoryCount

  // Handle expand/collapse all
  const handleToggleAllCategories = () => {
    if (allCategoriesExpanded) {
      collapseAllCategories()
    } else {
      expandAllCategories()
    }
  }

  // Handle toggle all services
  const handleToggleAllServices = () => {
    if (activeServicesCount > 0) {
      clearAllServices()
    } else {
      activateAllServices()
    }
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#fafafa', minHeight: '600px' }}>
      {/* Header */}
      <Card
        style={{
          marginBottom: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}
        bodyStyle={{ padding: '20px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={3} style={{ margin: 0, color: '#1f1f1f' }}>
              Services Offered
            </Title>
            <Text style={{ color: '#666666', fontSize: '14px' }}>
              Configure which services this clinic offers to patients
            </Text>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size='small'
              onClick={handleToggleAllCategories}
              icon={<FontAwesomeIcon icon={allCategoriesExpanded ? faCompressArrowsAlt : faExpandArrowsAlt} />}
            >
              {allCategoriesExpanded ? 'Collapse All' : 'Expand All'}
            </Button>

            <Button
              size='small'
              type={activeServicesCount > 0 ? 'default' : 'primary'}
              onClick={handleToggleAllServices}
              icon={<FontAwesomeIcon icon={activeServicesCount > 0 ? faToggleOff : faToggleOn} />}
            >
              {activeServicesCount > 0 ? 'Deactivate All' : 'Activate All'}
            </Button>
          </div>
        </div>

        {/* Summary */}
        <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
          <Text style={{ fontSize: '13px', color: '#666666' }}>
            <strong>{activeServicesCount}</strong> services currently active across <strong>{categoryCount}</strong>{' '}
            categories
          </Text>
        </div>
      </Card>

      {/* Services Categories */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {GlobalServicesEnums.categories.map((i) => {
          // map through categories here
          const linkedServices = globalServices.filter((service) => service.category === i.value)

          // Skip empty categories
          if (linkedServices.length === 0) return null

          return (
            <ServiceCategory
              key={i.value}
              category={i}
              services={linkedServices}
              activeServices={activeServices}
              onServiceToggle={toggleService}
              onCategoryToggle={toggleCategoryExpansion}
              isExpanded={expandedCategories.includes(i.value)}
              handleToggleAllCategoryServices={handleToggleAllCategoryServices}
            />
          )
        })}
      </div>

      {/* Empty State */}
      {/* {Object.keys(servicesByCategory).length === 0 && (
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <Text style={{ color: '#999999' }}>No services available</Text>
        </Card>
      )} */}
    </div>
  )
}

export default ServicesOfferedTab
